import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrganigrammeList({ token, isAdmin }) {
  const [organigrammes, setOrganigrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const fetchData = async () => {
    try {
      const res = await api.get('/organigrammes');
      setOrganigrammes(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey]);

  const triggerSync = () => setRefreshKey(k => k + 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/organigrammes/${editItem.id}`, formData);
        toast.success('Organigramme modifié');
      } else {
        await api.post('/organigrammes', formData);
        toast.success('Organigramme créé');
      }
      setShowForm(false);
      setEditItem(null);
      setFormData({ name: '', description: '' });
      triggerSync();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/organigrammes/${id}`);
      toast.success('Organigramme supprimé');
      setConfirmDeleteId(null);
      triggerSync();
    } catch (e) { toast.error('Erreur'); }
  };

  // Count nodes per organigramme
  const [nodeCounts, setNodeCounts] = useState({});
  useEffect(() => {
    if (organigrammes.length === 0) { setNodeCounts({}); return; }
    Promise.all(
      organigrammes.map(org => api.get(`/orgchart?organigramme_id=${org.id}`).then(r => [org.id, r.data.length]))
    ).then(results => {
      const counts = {};
      results.forEach(([id, count]) => { counts[id] = count; });
      setNodeCounts(counts);
    }).catch(() => {});
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [organigrammes, refreshKey]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="organigrammes-list-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-uppercase mb-2">Structure</p>
          <h1 className="font-heading text-4xl">Organigrammes</h1>
        </div>
        {isAdmin && (
          <button onClick={() => { setEditItem(null); setFormData({ name: '', description: '' }); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors"
            data-testid="create-organigramme-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
            Nouvel organigramme
          </button>
        )}
      </div>

      {/* Global overview removed at user request — only individual organigrammes remain below */}

      {/* Grid of organigrammes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="organigrammes-grid">
        {organigrammes.map(org => (
          <div key={org.id}
            className="group bg-white border border-border hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate(`/orgchart/${org.id}`)}
            data-testid={`organigramme-card-${org.id}`}>
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="font-heading text-xl group-hover:text-gold-dark transition-colors">{org.name}</h2>
                  {org.description && (
                    <p className="text-sm text-muted-foreground mt-1">{org.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M3 7h18M3 12h18M3 17h18" /></svg>
                  {nodeCounts[org.id] || 0} noeuds
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">
                  {org.created_at ? new Date(org.created_at).toLocaleDateString('fr-FR') : ''}
                </span>
                {isAdmin && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    <button onClick={() => { setEditItem(org); setFormData({ name: org.name, description: org.description || '' }); setShowForm(true); }}
                      className="px-2 py-1 text-[11px] border border-border hover:bg-muted transition-colors"
                      data-testid={`edit-organigramme-${org.id}`}>
                      Modifier
                    </button>
                    <button onClick={() => setConfirmDeleteId(org.id)}
                      className="px-2 py-1 text-[11px] border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                      data-testid={`delete-organigramme-${org.id}`}>
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {organigrammes.length === 0 && (
          <div className="col-span-full text-center py-16 text-muted-foreground">
            <svg className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24"><path d="M3 7l9-4 9 4M3 7v10l9 4M3 7l9 4m0 0l9-4m-9 4v10m9-14v10l-9 4" /></svg>
            <p className="text-lg">Aucun organigramme</p>
            {isAdmin && <p className="text-sm mt-1">Créez votre premier organigramme pour commencer</p>}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
          <div className="bg-white p-8 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-2xl mb-6">{editItem ? 'Modifier' : 'Créer'} un organigramme</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-uppercase block mb-1">Nom</label>
                <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} required
                  placeholder="Ex: Groupe KEMPF, Projet Atlantique..."
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none"
                  data-testid="organigramme-input-name" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Description</label>
                <textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))}
                  placeholder="Description optionnelle..."
                  className="w-full border border-input px-3 py-2 min-h-20 resize-none focus:border-primary focus:outline-none"
                  data-testid="organigramme-input-description" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 uppercase tracking-wider text-sm hover:bg-primary/90"
                  data-testid="organigramme-submit-btn">
                  {editItem ? 'Modifier' : 'Créer'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-border px-6 py-2 text-sm hover:bg-muted">Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-xl mb-3">Supprimer cet organigramme ?</h2>
            <p className="text-sm text-muted-foreground mb-4">Toutes les sociétés, liens et associés de cet organigramme seront supprimés.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDeleteId)}
                className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90"
                data-testid="confirm-delete-organigramme-btn">Supprimer</button>
              <button onClick={() => setConfirmDeleteId(null)}
                className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
